import React, { Dispatch, SetStateAction } from "react";
import styled from "@emotion/styled";

import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import useAppSelector from "../../../hooks/useAppSelector";
import { RootState } from "../../../redux/store";
import { File } from "../../../types/app";

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 250px;
  margin-top: 10px;
`;

const DisplayText = styled(Typography)`
  margin-top: 12px;
  margin-bottom: 3px;
`;

const Button = styled(MuiButton)<{ selected: boolean }>`
  background-color: ${(props) =>
    props.selected
      ? props.theme.palette.primary
      : props.theme.palette.background.default};
  color: ${(props) =>
    props.selected
      ? props.theme.palette.background.default
      : props.theme.palette.primary.main};
  &:hover {
    background-color: ${(props) =>
      props.selected
        ? props.theme.palette.primary.main
        : props.theme.palette.secondary.main};
    color: ${(props) => props.theme.palette.background.default};
  }
`;

const CheckboxSection = styled.div`
  max-height: 400px;
  margin-top: 5px;
  margin-left: 3px;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CheckButton = styled(MuiButton)`
  height: 26px;
  margin-top: 4px;
  font-size: 12px;
  padding: 10px;
`;

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  clusterDisplay: Set<string>;
  setClusterDisplay: Dispatch<SetStateAction<Set<string>>>;
  pdfDisplay: Set<string>;
  setPdfDisplay: Dispatch<SetStateAction<Set<string>>>;
  orderBy: string;
  setOrderBy: Dispatch<SetStateAction<string>>;
}

function FilterDialog(prop: Props) {
  const {
    open,
    setOpen,
    clusterDisplay,
    setClusterDisplay,
    pdfDisplay,
    setPdfDisplay,
    orderBy,
    setOrderBy,
  } = prop;
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const clusters = searchState.clusters;
  const files = state.files;

  function handleClusterCheckboxClick(
    e: React.SyntheticEvent,
    cluster: string
  ) {
    const updatedClusterDisplay = new Set(clusterDisplay);
    if (clusterDisplay.has(cluster)) {
      updatedClusterDisplay.delete(cluster);
    } else {
      updatedClusterDisplay.add(cluster);
    }
    setClusterDisplay(updatedClusterDisplay);
  }

  function handleFileCheckboxClick(e: React.SyntheticEvent, file: string) {
    const updatedPdfDisplay = new Set(pdfDisplay);
    if (pdfDisplay.has(file)) {
      updatedPdfDisplay.delete(file);
    } else {
      updatedPdfDisplay.add(file);
    }
    setPdfDisplay(updatedPdfDisplay);
  }

  function checkAllClusters() {
    setClusterDisplay(new Set(clusters));
  }

  function checkAllFiles() {
    setPdfDisplay(new Set(files.map((file: File) => file.fileName)));
  }

  function uncheckAllClusters() {
    setClusterDisplay(new Set());
  }

  function uncheckAllFiles() {
    setPdfDisplay(new Set());
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Display Configurations</DialogTitle>
      <DialogContent sx={{ overflow: "hidden" }}>
        <Box>
          <Typography>Order By: </Typography>
          <ButtonGroup>
            <Button
              variant="contained"
              selected={orderBy === "Confidence"}
              onClick={() => setOrderBy("Confidence")}
            >
              Confidence
            </Button>
            <Button
              variant="contained"
              selected={orderBy === "Cluster"}
              onClick={() => setOrderBy("Cluster")}
            >
              Cluster
            </Button>
          </ButtonGroup>
        </Box>
        <DisplayText>Clusters to Display:</DisplayText>
        <CheckButton variant="contained" onClick={() => checkAllClusters()}>
          Check All
        </CheckButton>
        <CheckButton
          variant="contained"
          onClick={() => uncheckAllClusters()}
          sx={{ marginLeft: "10px" }}
        >
          Uncheck All
        </CheckButton>
        <CheckboxSection>
          {clusters.map((cluster: string) => {
            return (
              <FormControlLabel
                key={cluster}
                control={<Checkbox />}
                label={<Typography>{cluster}</Typography>}
                checked={clusterDisplay.has(cluster)}
                onChange={(event) => handleClusterCheckboxClick(event, cluster)}
              />
            );
          })}
        </CheckboxSection>
        <DisplayText>Files to Display:</DisplayText>
        <CheckButton variant="contained" onClick={() => checkAllFiles()}>
          Check All
        </CheckButton>
        <CheckButton
          variant="contained"
          onClick={() => uncheckAllFiles()}
          sx={{ marginLeft: "10px" }}
        >
          Uncheck All
        </CheckButton>
        <CheckboxSection>
          {files.map((file: File) => {
            return (
              <FormControlLabel
                key={file.fileUid}
                control={<Checkbox />}
                label={
                  <Typography noWrap textOverflow="ellipsis" maxWidth="450px">
                    {file.fileName}
                  </Typography>
                }
                checked={pdfDisplay.has(file.fileName)}
                onChange={(event) =>
                  handleFileCheckboxClick(event, file.fileName)
                }
              />
            );
          })}
        </CheckboxSection>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={() => setOpen(false)} color="primary">
          Close
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}

export default FilterDialog;
